import { useState, useCallback, useEffect } from "react";
import { useAlert } from "../functions/useAlert";
import { fetchDocTemplates } from "../services/docTemplates.service";
import { fetchClients } from "../services/clients.service";
import { fetchOrgContacts } from "../services/contacts.service";
import { fetchMeetingsByOrg } from "../services/meetings.service";
import { fetchAdvisors } from "../services/users.service";

interface UseHomepageDataOptions {
  login: any;
}

export function useHomepageData({ login }: UseHomepageDataOptions) {
  const [meetings, setMeetings] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [advisors, setAdvisors] = useState<any[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<any[]>([]);
  const [isClientsLoading, setIsClientsLoading] = useState(true);
  const [isMeetingsLoading, setIsMeetingsLoading] = useState(true);
  const [isAllContactsLoading, setIsAllContactsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"my" | "all">("my");

  const [setAlert] = useAlert();

  const fetchMeetingTypes = useCallback(async () => {
    try {
      const response: any = await fetchDocTemplates(login.data.orgId);
      let filteredTemplates = response.data || [];
      if (Array.isArray(filteredTemplates)) {
        filteredTemplates = filteredTemplates.filter(
          (type: any) => type.templateType === "file note",
        );
      }
      setMeetingTypes(filteredTemplates);
    } catch {
      setAlert("Failed to load meeting types.", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAlert, login?.data?.orgId]);

  const fetchClientsData = useCallback(async () => {
    setIsClientsLoading(true);
    try {
      const clientsResponse: any = await fetchClients(login.data.orgId);
      setClients(clientsResponse || []);
    } catch {
      setAlert("Failed to load clients.", "error");
    } finally {
      setIsClientsLoading(false);
    }
  }, [login.data.orgId, setAlert]);

  const fetchAllContactsData = useCallback(async () => {
    setIsAllContactsLoading(true);
    try {
      const response: any = await fetchOrgContacts(login.data.orgId);
      setAllContacts(response.contacts || []);
    } catch {
      setAlert("Failed to load contacts.", "error");
    } finally {
      setIsAllContactsLoading(false);
    }
  }, [login.data.orgId, setAlert]);

  const fetchMeetingsData = useCallback(async () => {
    setIsMeetingsLoading(true);
    try {
      const advisorId = viewMode === "my" ? login.data.id : undefined;
      const meetingsResponse: any = await fetchMeetingsByOrg(login.data.orgId, advisorId);
      setMeetings(meetingsResponse.meetings || []);
    } catch {
      setAlert("Failed to load meetings data.", "error");
    } finally {
      setIsMeetingsLoading(false);
    }
  }, [login.data.orgId, login.data.id, setAlert, viewMode]);

  const fetchAdvisorsData = useCallback(async () => {
    try {
      const advisorsResponse: any = await fetchAdvisors(login.data.orgId);
      setAdvisors(advisorsResponse.users || []);
    } catch {
      setAlert("Failed to load advisors.", "error");
    }
  }, [login.data.orgId, setAlert]);

  useEffect(() => {
    fetchClientsData();
    fetchAdvisorsData();
    fetchAllContactsData();
    fetchMeetingTypes();
  }, [fetchClientsData, fetchAdvisorsData, fetchAllContactsData, fetchMeetingTypes]);

  useEffect(() => {
    fetchMeetingsData();
  }, [fetchMeetingsData]);

  return {
    meetings,
    setMeetings,
    clients,
    setClients,
    allContacts,
    setAllContacts,
    advisors,
    meetingTypes,
    isClientsLoading,
    isMeetingsLoading,
    isAllContactsLoading,
    viewMode,
    setViewMode,
    fetchMeetingsData,
    fetchClientsData,
    fetchAllContactsData,
    fetchAdvisorsData,
    fetchMeetingTypes,
  };
}
